import React, { useContext, useEffect, useState, useRef } from "react";
import {
  dashboardItems,
  maintenanceItems,
} from "../controlPanel/dashboardData";
import DiagnosticCards from "./DiagnosticCards";
import ContentCardd from "./ContentCardd";
import api from "../../services/AxiosConnection";
import AuthContext from "../../context/AuthContext";
import { useLocation } from "react-router-dom";
import { getDiagnosticItems } from "./diagnosticsdata";
import { Vortex } from "react-loader-spinner";
import socket from "../../services/socket.js";

const STATUS = {
  IDLE: "idle",
  IN_PROGRESS: "in-progress",
  LOADING: "loading",
  FINAL: "final",
};

const DiagnosticCard = ({ status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case STATUS.IN_PROGRESS:
        return "bg-red-500";
      case STATUS.LOADING:
        return "bg-yellow-500";
      case STATUS.FINAL:
        return "bg-green-500";
      default:
        return "bg-gray-200";
    }
  };
  return (
    <div
      className={`h-32 rounded-lg shadow-md transition-all duration-300 ${getStatusColor(
        status
      )}`}
    />
  );
};

function Diagnostics() {
  const [isPlaying, setIsPlaying] = useState(false);
  const location = useLocation();
  const { siteDb: routeSiteDb } = location.state || {};
  const { selectedSite, setSelectedSite } = useContext(AuthContext);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardStatuses, setCardStatuses] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const intervalRef = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const allCards = [...dashboardItems, ...maintenanceItems];

  const handlePlayPause = () => {
    setIsPlaying((prev) => !prev);
    if (showSuccess) setShowSuccess(false);
  };

  // Function to fetch diagnostic data
  const fetchDiagnosticData = async (showLoading = true) => {
    if (!selectedSite) return;

    if (showLoading) setLoading(true);

    try {
      const response = await api.get("/diagnostic/getDiagnoticData", {
        params: {
          siteDb: selectedSite,
        },
      });

      setDiagnosticData(response.data);
    } catch (error) {
      console.error("Error fetching diagnostic data:", error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };
  useEffect(() => {
    if (!selectedSite) return;

    fetchDiagnosticData(true);

    if (socket.connected) {
      console.log(`[Diagnostics] Socket connected. Joining site room: ${selectedSite}`);
      socket.emit("join_site", selectedSite);
    }

    const handleConnect = () => {
      console.log(`[Diagnostics] Socket reconnected. Joining site room: ${selectedSite}`);
      socket.emit("join_site", selectedSite);
    };

    const handleDataUpdated = (updatedData) => {
      if (!updatedData || (updatedData.siteDb && updatedData.siteDb !== selectedSite)) return;
      console.log(`[Diagnostics] Real-time socket update received for ${selectedSite}. Refreshing diagnostic data.`);
      fetchDiagnosticData(false);
    };

    socket.on("connect", handleConnect);
    socket.on("division_data_updated", handleDataUpdated);
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const intervalId = setInterval(() => {
      console.log(`[Diagnostics] 5-minute background interval triggered. Refreshing diagnostic data.`);
      fetchDiagnosticData(false);
    }, FIVE_MINUTES_MS);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("division_data_updated", handleDataUpdated);
      clearInterval(intervalId);
    };
  }, [selectedSite]);

  useEffect(() => {
    if (routeSiteDb && routeSiteDb !== selectedSite) {
      setSelectedSite(routeSiteDb);
    }
  }, [routeSiteDb]);

  const { diagnosticlItems, diagnosticRItems } =
    getDiagnosticItems(diagnosticData);

  useEffect(() => {
    if (diagnosticlItems.length > 0 && !selectedItem) {
      setSelectedItem(diagnosticlItems[0]);
    }
  }, [diagnosticlItems]);

  const resetDiagnostics = () => {
    setCurrentIndex(0);
    setCardStatuses({});
    setIsPlaying(false);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          if (prevIndex >= allCards.length - 1) {
            const lastCard = allCards[prevIndex];
            setCardStatuses((prev) => ({
              ...prev,
              [lastCard.id]: STATUS.IN_PROGRESS,
            }));
            setTimeout(() => {
              setCardStatuses((prev) => ({
                ...prev,
                [lastCard.id]: STATUS.LOADING,
              }));
            }, 1000);
            setTimeout(() => {
              setCardStatuses((prev) => ({
                ...prev,
                [lastCard.id]: STATUS.FINAL,
              }));
              setTimeout(() => {
                resetDiagnostics();
              }, 2000);
            }, 2000);
            clearInterval(intervalRef.current);
            return prevIndex;
          }
          const newIndex = prevIndex + 1;
          setCardStatuses((prev) => ({
            ...prev,
            [allCards[prevIndex].id]: STATUS.IN_PROGRESS,
          }));
          setTimeout(() => {
            setCardStatuses((prev) => ({
              ...prev,
              [allCards[prevIndex].id]: STATUS.LOADING,
            }));
          }, 1000);
          setTimeout(() => {
            setCardStatuses((prev) => ({
              ...prev,
              [allCards[prevIndex].id]: STATUS.FINAL,
            }));
          }, 2000);
          return newIndex;
        });
      }, 3000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying]);

  return (
    <div className="bg-white p-4 md:p-6 h-[calc(100vh-4rem-3rem-4rem)] overflow-hidden flex flex-col">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <Vortex
            visible={true}
            height={80}
            width={80}
            ariaLabel="vortex-loading"
            wrapperStyle={{}}
            wrapperClass="vortex-wrapper"
            colors={["#2563eb", "#1e40af", "#60a5fa", "#3b82f6", "#1d4ed8", "#93c5fd"]}
          />
        </div>
      )}



      <div className="grid grid-cols-12 gap-4 md:gap-6 flex-grow min-h-0">
        <div className="col-span-12 md:col-span-3 h-full">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-gray-800">System Diagnostics</h2>
          </div>
          <div className="space-y-4">
            {diagnosticlItems.map((item) => (
              <DiagnosticCards
                key={item.id}
                Icon={item.icon}
                title={item.title}
                description={item.description}
                status={item.status}
                onClick={() => handleItemClick(item)}
                isSelected={selectedItem?.id === item.id}
              />
            ))}
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 h-full min-h-0">
          <ContentCardd
            content={selectedItem}
            diagnosticData={diagnosticData}
          />
        </div>
        <div className="col-span-12 md:col-span-3 h-full">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-gray-800">Software Diagnostic</h2>
          </div>
          <div className="space-y-4">
            {diagnosticRItems.map((item) => (
              <DiagnosticCards
                key={item.id}
                Icon={item.icon}
                title={item.title}
                description={item.description}
                status={item.status}
                onClick={() => handleItemClick(item)}
                isSelected={selectedItem?.id === item.id}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Diagnostics;